import themes from 'devextreme/ui/themes';
import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { isChunkLoadError } from './app/services/app-update/chunk-load-error';

Sentry.init({
  dsn: environment.sentryDsn,
  environment: environment.name,
  // dev and prod now both carry the real crystal-web DSN. Guard on a plausible DSN shape rather
  // than just truthiness anyway, as defense-in-depth against a placeholder being reintroduced --
  // local dev's environment.ts/environment.local-noauth.ts still have a genuinely empty string.
  enabled: environment.sentryDsn.startsWith('https://'),
  integrations: [
    Sentry.browserTracingIntegration(),
    // Sweeps up existing console.error call sites with no code changes at those call sites.
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
    // The video of what led to a bug. Text, inputs and media are masked: Crystal shows client,
    // case and billing data, and none of it may leave the browser in a replay. The replay still
    // shows clicks, navigation and layout; the error itself carries the technical detail.
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
    }),
  ],
  // Sentry's own global handlers and the console.error integration see chunk-load failures
  // (stale tab after a deploy) before the Angular ErrorHandler does; drop them here too.
  beforeSend: (event, hint) => {
    const values = event.exception?.values ?? [];
    const stale =
      isChunkLoadError(hint?.originalException) ||
      values.some((v) => isChunkLoadError(`${v.type ?? ''} ${v.value ?? ''}`)) ||
      isChunkLoadError(event.message);
    return stale ? null : event;
  },
  tracesSampleRate: 0.1,
  // Record only when an error happens: the replay keeps the ~1 minute before it and is linked to
  // the error event (and so to its error_reference). No routine session recording.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  // Links a frontend transaction to the backend transaction it calls, so one failed save is
  // one story across both Sentry projects rather than two unconnected events.
  tracePropagationTargets: [environment.CalystaApiBaseURL],
  // Same rule as the backend: identifiers only. Do not enable sendDefaultPii.
  sendDefaultPii: false,
});

if (environment.production) {
  enableProdMode();
}

themes.initialized(() => {
  platformBrowserDynamic()
    .bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()], })
    .catch((err) => console.error(err));
});
